// Panel lateral derecho (drawer) con el detalle de un documento.
// Permite cambiar el estado de validación, escribir una nota (máx 500
// caracteres con autoresize), guardar/cancelar y abrir el archivo en
// Word (deep link al webUrl de SharePoint). Cerrar con clic en overlay,
// botón "×" o tecla Escape.

import { html } from 'htm/preact'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'

import { StatusBadge, toLabel } from './StatusBadge.js'
import { StatusSelector } from './StatusSelector.js'
import {
  getDocumentEntry,
  setDocumentState,
  subscribeValidation
} from '../lib/validation-store.js'
import { getActiveAccount } from '../auth/auth-provider.js'
import { showToast } from '../lib/toast-bus.js'

const NOTAS_MAX = 500
const NOTAS_TEXTAREA_MAX_HEIGHT = 320

const CLOSE_ICON = html`
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
`

function autosize(el) {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${Math.min(el.scrollHeight, NOTAS_TEXTAREA_MAX_HEIGHT)}px`
}

function MetadataItem({ label, value }) {
  if (value == null || value === '') return null
  return html`
    <li class="drawer__metadata-item">
      <span class="drawer__metadata-key">${label}</span>
      <span class="drawer__metadata-value">${value}</span>
    </li>
  `
}

function HistorialItem({ entry, latest }) {
  // Compatibilidad: entries antiguas pueden traer `revisor` en lugar de
  // `modificadoPor`, y pueden no tener `modificadoPorEmail`.
  const autor = entry.modificadoPor || entry.revisor || '—'
  const email = entry.modificadoPorEmail || ''
  return html`
    <li class=${`drawer__history-item ${latest ? 'drawer__history-item--latest' : ''}`}>
      <div
        class="drawer__history-fecha"
        title=${email ? `${autor} · ${email}` : autor}
      >
        ${new Date(entry.fecha).toLocaleString('es-MX')} · ${autor}
        ${email ? html`<span class="drawer__history-email"> · ${email}</span>` : null}
      </div>
      <div class="drawer__history-cambio">
        ${entry.estado_anterior
          ? html`${toLabel(entry.estado_anterior)} → ${toLabel(entry.estado_nuevo)}`
          : html`Estado inicial: ${toLabel(entry.estado_nuevo)}`}
      </div>
      ${entry.nota
        ? html`<div class="drawer__history-nota">${entry.nota}</div>`
        : null}
    </li>
  `
}

export function DocumentDetail({ file, onClose }) {
  const account = useMemo(() => getActiveAccount(), [])
  // Identidad del revisor: nombre legible + correo único (UPN). Persistir
  // ambos permite escalar a App Roles en v2.0 sin colisiones de nombre.
  const modificadoPor = account?.name || account?.username || 'Desconocido'
  const modificadoPorEmail = account?.username || ''
  const textareaRef = useRef(null)

  const persisted = getDocumentEntry(file)
  const [estadoLocal, setEstadoLocal] = useState(persisted?.estado || null)
  const [nota, setNota] = useState(persisted?.notas || '')
  const [saving, setSaving] = useState(false)
  const [, forceRender] = useState(0)

  // Re-render al recibir notificaciones del store (flush queue, etc.).
  useEffect(() => subscribeValidation(() => forceRender((v) => v + 1)), [])

  // Reset del formulario al cambiar de archivo.
  useEffect(() => {
    const entry = getDocumentEntry(file)
    setEstadoLocal(entry?.estado || null)
    setNota(entry?.notas || '')
    requestAnimationFrame(() => autosize(textareaRef.current))
  }, [file?.id])

  // Autoresize del textarea cada vez que cambia el texto.
  useEffect(() => {
    autosize(textareaRef.current)
  }, [nota])

  // Cierre con Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const meta = file.metadata || {}
  const entry = getDocumentEntry(file)
  const estadoPersistido = entry?.estado || null
  const historial = Array.isArray(entry?.historial)
    ? [...entry.historial].reverse()
    : []

  const dirty =
    (estadoLocal || null) !== (estadoPersistido || null) ||
    (nota || '') !== (entry?.notas || '')

  const handleSave = async () => {
    if (!estadoLocal) {
      showToast({
        message: 'Selecciona un estado antes de guardar.',
        kind: 'warning'
      })
      return
    }
    setSaving(true)
    try {
      await setDocumentState(file, {
        estado: estadoLocal,
        nota,
        modificadoPor,
        modificadoPorEmail
      })
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const e = getDocumentEntry(file)
    setEstadoLocal(e?.estado || null)
    setNota(e?.notas || '')
  }

  const handleOpenWord = () => {
    if (!file.webUrl) {
      showToast({
        message: 'El documento no tiene webUrl disponible en Graph.',
        kind: 'error'
      })
      return
    }
    window.open(file.webUrl, '_blank', 'noopener,noreferrer')
  }

  return html`
    <div
      class="drawer__overlay"
      onClick=${() => onClose?.()}
      aria-hidden="true"
    ></div>
    <aside
      class="drawer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="drawer-title"
    >
      <header class="drawer__header">
        <div>
          <div class="drawer__eyebrow">Detalle de documento</div>
          <h2 class="drawer__title" id="drawer-title">${file.name}</h2>
        </div>
        <button
          type="button"
          class="drawer__close"
          onClick=${() => onClose?.()}
          aria-label="Cerrar detalle"
          title="Cerrar (Esc)"
        >${CLOSE_ICON}</button>
      </header>

      <div class="drawer__body">
        <section class="drawer__section">
          <h3 class="drawer__section-title">Metadata</h3>
          <ul class="drawer__metadata-list">
            <${MetadataItem} label="Ruta" value=${file.relativePath} />
            <${MetadataItem} label="Tipo" value=${meta.tipo} />
            <${MetadataItem} label="Fase" value=${meta.fase} />
            <${MetadataItem} label="Audiencia" value=${meta.audiencia} />
            <${MetadataItem} label="Cliente" value=${meta.cliente} />
            <${MetadataItem} label="Código entregable" value=${meta.codigoEntregable} />
            <${MetadataItem}
              label="Sección AFAC"
              value=${meta.seccionAFAC
                ? `Sección ${String(meta.seccionAFAC).padStart(2, '0')}`
                : null}
            />
            <${MetadataItem} label="Versión" value=${meta.version} />
            <${MetadataItem} label="Es plantilla" value=${meta.esPlantilla ? 'Sí' : 'No'} />
            <${MetadataItem}
              label="Última modificación SharePoint"
              value=${file.lastModifiedDateTime
                ? new Date(file.lastModifiedDateTime).toLocaleString('es-MX')
                : null}
            />
          </ul>
        </section>

        <section class="drawer__section">
          <h3 class="drawer__section-title">Estado de validación</h3>
          <div class="drawer__current-state">
            <span class="drawer__state-label">Actual:</span>
            <${StatusBadge} estado=${estadoPersistido} />
            ${entry?.ultimaModificacion
              ? html`
                  <span
                    class="drawer__state-meta"
                    title=${entry.modificadoPorEmail
                      ? `${entry.modificadoPor || ''} · ${entry.modificadoPorEmail}`
                      : entry.modificadoPor || ''}
                  >
                    actualizado el ${new Date(entry.ultimaModificacion).toLocaleString('es-MX')}
                    ${entry.modificadoPor ? html` por ${entry.modificadoPor}` : null}
                    ${entry.modificadoPorEmail
                      ? html`<span class="drawer__state-meta-email"> (${entry.modificadoPorEmail})</span>`
                      : null}
                  </span>
                `
              : null}
          </div>
          <${StatusSelector} value=${estadoLocal} onChange=${setEstadoLocal} />
          <label class="drawer__notes-label" for="drawer-notes">Nota del revisor</label>
          <textarea
            id="drawer-notes"
            ref=${textareaRef}
            class="drawer__notes"
            maxLength=${NOTAS_MAX}
            placeholder="Notas opcionales (motivo del cambio, instrucciones para Cowork, etc.)"
            value=${nota}
            onInput=${(e) => setNota(e.target.value)}
          ></textarea>
          <div class="drawer__notes-counter">
            ${nota.length} / ${NOTAS_MAX} caracteres
          </div>
          <div class="drawer__actions">
            <button
              type="button"
              class="btn btn--primary"
              onClick=${handleSave}
              disabled=${saving || !dirty}
            >${saving ? 'Guardando…' : 'Guardar'}</button>
            <button
              type="button"
              class="btn btn--secondary"
              onClick=${handleCancel}
              disabled=${!dirty || saving}
            >Cancelar</button>
            <button
              type="button"
              class="btn btn--secondary"
              onClick=${handleOpenWord}
            >Abrir en Word</button>
          </div>
        </section>

        <section class="drawer__section">
          <h3 class="drawer__section-title">Historial</h3>
          ${historial.length === 0
            ? html`<p class="drawer__empty">Sin cambios registrados todavía.</p>`
            : html`
                <ol class="drawer__history">
                  ${historial.map(
                    (h, idx) => html`
                      <${HistorialItem}
                        key=${h.fecha + idx}
                        entry=${h}
                        latest=${idx === 0}
                      />
                    `
                  )}
                </ol>
              `}
        </section>
      </div>
    </aside>
  `
}
